import { z } from 'zod'

/**
 * Step types — thêm một type mới ở đây là mở rộng duy nhất cần cho một luồng mới.
 * Không có `if (partner)` ở bất kỳ đâu: partner khác nhau = mảng steps khác nhau.
 */
export const StepType = z.enum(['info', 'link_account', 'ekyc', 'review', 'sign'])
export type StepType = z.infer<typeof StepType>

/** Field mà step `info` / `review` được phép render. Whitelist — không nhận field tự do. */
export const FieldKey = z.enum([
  'email',
  'phone',
  'otp',
  'bank_account',
  'bank_name',
  'account_name',
  'bank_branch',
  'full_name',
  'id_no',
  'dob',
  'id_issued_date',
  'id_issued_place',
  'tax_code',
  'id_images',
  'address',
])
export type FieldKey = z.infer<typeof FieldKey>

/**
 * Field là CREDENTIAL chứ không phải dữ liệu hồ sơ.
 *
 * `otp` nằm trong `fields` để khai báo "bước này cần OTP", nhưng KHÔNG bao giờ được nằm
 * trong `editable`. `editable` nghĩa là "field của hồ sơ mà user được sửa" — hấp thụ OTP
 * vào hồ sơ là lưu một mã dùng một lần thành dữ liệu khách hàng.
 *
 * Hệ quả hai phía: FE luôn cho nhập (không readonly), BE luôn từ chối ghi vào `subject`.
 */
const CREDENTIAL_FIELDS = new Set<string>(['otp'])
export const isCredentialField = (name: string): boolean => CREDENTIAL_FIELDS.has(name)

/** `editable` chứa credential là cấu hình sai — chặn ngay lúc parse, không đợi tới runtime. */
const noCredentialInEditable = <T extends { editable: FieldKey[] }>(config: T, ctx: z.RefinementCtx): void => {
  for (const name of config.editable) {
    if (isCredentialField(name)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['editable'],
        message: `"${name}" là mã xác thực, không phải dữ liệu hồ sơ — không được đưa vào editable`,
      })
    }
  }
}

const InfoConfig = z
  .object({
    fields: z.array(FieldKey).min(1),
    /** Field user được sửa. Phần còn lại readonly — bank đã KYC nên phần lớn readonly. */
    editable: z.array(FieldKey).default([]),
    security_note: z.string().optional(),
  })
  .superRefine(noCredentialInEditable)

const LinkAccountConfig = z.object({
  /** Hệ thống đối tác hiển thị trong 3-node graphic. */
  target_system: z.string(),
  support_url: z.string().optional(),
})

const EkycWebviewUrl = z.string().min(1).refine(
  (value) => value.startsWith('/') || /^https?:\/\/[^\s]+$/i.test(value),
  'webview_url phải là đường dẫn cùng origin hoặc URL http(s)',
)

const EkycAllowedOrigin = z.string().url().refine((value) => {
  const url = new URL(value)
  return ['http:', 'https:'].includes(url.protocol) && url.origin === value
}, 'allowed_origin phải là origin http(s), không chứa path')

const EkycConfig = z.object({
  provider: z.string(),
  /** Trang eKYC của provider được nhúng như một webview/iframe độc lập. */
  webview_url: EkycWebviewUrl.optional(),
  /** Origin duy nhất được phép gửi postMessage. Bỏ trống thì suy ra từ webview_url. */
  allowed_origin: EkycAllowedOrigin.optional(),
  /** Số ảnh giấy tờ: 2 = CCCD 2 mặt. */
  document_sides: z.number().int().min(1).max(2),
  require_selfie: z.boolean(),
})

const ReviewConfig = z
  .object({
    fields: z.array(FieldKey).min(1),
    editable: z.array(FieldKey).default([]),
  })
  .superRefine(noCredentialInEditable)

const SignConfig = z.object({
  method: z.enum(['otp', 'ca', 'click']),
  provider: z.string().optional(),
})

/**
 * Discriminated union: mỗi step type có config riêng, sai shape là parse fail
 * ngay ở biên BE↔FE thay vì crash giữa flow.
 */
export const Step = z.discriminatedUnion('type', [
  z.object({ type: z.literal('info'), label: z.string(), config: InfoConfig }),
  z.object({ type: z.literal('link_account'), label: z.string(), config: LinkAccountConfig }),
  z.object({ type: z.literal('ekyc'), label: z.string(), config: EkycConfig }),
  z.object({ type: z.literal('review'), label: z.string(), config: ReviewConfig }),
  z.object({ type: z.literal('sign'), label: z.string(), config: SignConfig }),
])
export type Step = z.infer<typeof Step>

export const Flow = z.object({
  id: z.string(),
  steps: z.array(Step).min(1),
})
export type Flow = z.infer<typeof Flow>

/** Capability mà flow đòi hỏi ở container. Dùng để validate ở BE lúc tạo session. */
export const Capability = z.enum(['camera', 'file_upload', 'download'])
export type Capability = z.infer<typeof Capability>

/**
 * Capability BẮT BUỘC của từng step — nguồn duy nhất cho luật "capability quyết container".
 *
 * Chỉ liệt kê thứ mà thiếu nó thì step KHÔNG chạy được. `download` không nằm ở đây:
 * bước `sign` vẫn ký được trong WebView không tải file, chỉ là không hiện link tải PDF.
 * Đó là chuyện step tự kiểm `capabilities.download` rồi ẩn nút — không phải lý do chặn cả luồng.
 */
export const STEP_REQUIRED_CAPABILITIES: Record<StepType, Capability[]> = {
  info: [],
  link_account: [],
  ekyc: ['camera'],
  review: [],
  sign: [],
}
